export class BemElement<TElement extends Element = Element> {
	constructor(
		public readonly name: string,
		public readonly element: TElement,
		public readonly detachedModifiers = false
	) {}

	getModifierClass(modifier: string) {
		return this.detachedModifiers
			? `-${modifier}`
			: `${this.name}--${modifier}`;
	}

	addModifier(modifier: string) {
		this.element.classList.add(this.getModifierClass(modifier));
	}

	removeModifier(modifier: string) {
		this.element.classList.remove(this.getModifierClass(modifier));
	}

	hasModifier(modifier: string) {
		return this.element.classList.contains(this.getModifierClass(modifier));
	}

	toggleModifier(modifier: string, force?: boolean) {
		return this.element.classList.toggle(
			this.getModifierClass(modifier),
			force
		);
	}
}

export class BemBlock<
	TElement extends Element = Element
> extends BemElement<TElement> {
	constructor(name: string, element: TElement, detachedModifiers = false) {
		super(name, element, detachedModifiers);
	}

	getBemElementClass(name: string) {
		return `${this.name}__${name}`;
	}

	getBemElements<TType extends Element = Element>(name: string) {
		const elementName = this.getBemElementClass(name);

		return Array.from(this.element.getElementsByClassName(elementName)).map(
			(element) =>
				new BemElement(
					elementName,
					element as TType,
					this.detachedModifiers
				)
		);
	}

	getBemElement<TType extends Element = Element>(name: string) {
		const elementName = this.getBemElementClass(name);
		const element = this.element
			.getElementsByClassName(elementName)
			.item(0);

		return (
			element &&
			new BemElement(
				elementName,
				element as TType,
				this.detachedModifiers
			)
		);
	}

	eachBemElement<TType extends Element = Element>(
		name: string,
		callback: (
			element: BemElement<TType>,
			index: number,
			array: BemElement<TType>[]
		) => void
	) {
		this.getBemElements<TType>(name).forEach(callback);
	}

	static each<TElement extends Element = Element>(
		name: string,
		callback: (block: BemBlock<TElement>) => void,
		root: Element | Document = document,
		detachedModifiers = false
	) {
		for (const block of this.find<TElement>(
			name,
			root,
			detachedModifiers
		)) {
			callback(block);
		}
	}

	static find<TElement extends Element = Element>(
		name: string,
		root: Element | Document = document,
		detachedModifiers = false
	) {
		return Array.from(root.getElementsByClassName(name)).map(
			(element) =>
				new BemBlock(name, element as TElement, detachedModifiers)
		);
	}

	static findParent<TElement extends Element = Element>(
		element: Element,
		name: string,
		detachedModifiers?: boolean
	): BemBlock<TElement> | null;
	static findParent<TElement extends Element = Element>(
		element: BemElement,
		name: string,
		detachedModifiers?: boolean
	): BemBlock<TElement> | null;
	static findParent(
		element: Element | BemElement,
		name: string,
		detachedModifiers = false
	) {
		let parent = (element instanceof BemElement ? element.element : element)
			.parentElement;
		while (parent) {
			if (parent.classList.contains(name)) {
				return new BemBlock(name, parent, detachedModifiers);
			}
			parent = parent.parentElement;
		}
		return null;
	}
}
